import { ICellRendererComp, ICellRendererParams, SelectionChangedEvent } from 'ag-grid-community';
import { CellActionType, GenericColDef, GridContext } from '../../../models/grid-context';
import { OperationState, TaskStatus, TaskStatusTitle } from '../../../models/task';
import { isNullOrUndefined } from '../../../models/utils';
import { Job } from '../../../models/job';

export class GridWorkbasketActionsCellRenderer implements ICellRendererComp {
    params: ActionCellParams;
    colDef: GenericColDef;
    gridContext: GridContext;
    automationId: string;

    cellElement: HTMLElement;
    pauseButtonElement: HTMLElement;
    resumeButtonElement: HTMLElement;
    cancelButtonElement: HTMLElement;
    deleteButtonElement: HTMLElement;

    init?(params: ActionCellParams): void {
        this.params = params;
        this.cellElement = document.createElement('div');
        this.cellElement.className = 'grid-cell';

        if (this.params && this.params.data) {
            this.colDef = this.params.column.getColDef() as GenericColDef;

            this.gridContext = this.params.context as GridContext;
            if (this.colDef) {
                if (!this.colDef.automationIdParsed) {
                    this.colDef.automationIdParsed = true;
                    this.colDef.automationId = `${this.colDef.automationId ? (this.colDef.automationId + '-') : ''}grid-cell`;
                    let name = this.colDef.headerName;
                    name = name.replace(/[^a-zA-Z0-9-_ ]/gi, '');
                    if (name) {
                        this.colDef.automationId += `-${name.replace(/([a-z])([A-Z])/g, '$1-$2').replace(/\s+/g, '-').toLowerCase()}`;
                    }
                }
                this.automationId = this.colDef.automationId;

                this.cellElement.oncontextmenu = this.onCellContextMenu;
                this.cellElement.setAttribute('data-automation-id', this.automationId);

                if (this.params.data.tooltip) {
                    this.cellElement.title = this.params.data.tooltip;
                }
                this.createElement();
            }
        }
    }

    createElement(): void {
        const htmlParts = [];
        htmlParts.push(`<span class="workbasket-actions" data-automation-id="${this.automationId + '-label'}">`);
        htmlParts.push(`<div class="text">`);
        if (this.params.data.iconClass) {
            this.setIconClassViewDocList();
            const iconClass = (this.params.data.isDownloadableTask && (this.params.data.iconTitle === TaskStatusTitle.FAILURE || this.params.data.iconTitle === TaskStatusTitle.WARNING)) ?
                'flip-icon-workbasket-class ' + this.params.data.iconClass : (this.params.data.iconTitle === TaskStatusTitle.FAILURE || this.params.data.iconTitle === TaskStatusTitle.PAUSED_ON_FAILURE ||
                    this.params.data.tooltip === TaskStatusTitle.FAILURE || this.params.data.tooltip === TaskStatusTitle.PAUSED_ON_FAILURE) ?
                    'failure-info-class ' + this.params.data.iconClass : this.params.data.iconClass;
            let img = `<i class="fa ${iconClass}"`;
            const title = this.params.data.tooltip ? this.params.data.tooltip : this.params.data.iconTitle;
            if (title) {
                img += ` title="${(title === TaskStatusTitle.FAILURE || title === TaskStatusTitle.WARNING || title === TaskStatusTitle.PAUSED_ON_FAILURE) ?
                    title + '. Click for details.' : title}"`;
            }
            img += ` data-cell-action-type="${CellActionType.workbasketIcon}"></i>`;
            htmlParts.push(img);
            if (this.params.data.operationState === 'PAUSING' || this.params.data.taskStatus === 'PENDING_CANCELLATION' ||
                this.params.data.uploadState === 'Pausing' || this.params.data.uploadState === 'Cancelling' || this.params.data.uploadState === 'Resuming') {
                let secondImg = `<i class="second-icon fa fa-spinner"`;
                if (this.params.data.iconTitle) {
                    secondImg += ` title="${(this.params.data.iconTitle === TaskStatusTitle.FAILURE || this.params.data.iconTitle === TaskStatusTitle.WARNING || this.params.data.iconTitle === TaskStatusTitle.PAUSED_ON_FAILURE) ?
                        this.params.data.iconTitle + '. Click for details.' : this.params.data.iconTitle}"`;
                }
                secondImg += ` data-cell-action-type="${CellActionType.workbasketIcon}"></i>`;
                htmlParts.push(secondImg);
            } else if (this.params.data.isSearchableTask && this.params.data.taskStatus === TaskStatus.SUCCESS) {
                let secondImg = `<i`;
                if (this.params.data.operationState === OperationState.SUCCESS || isNullOrUndefined(this.params.data.operationState)) {
                    secondImg += ` class="second-icon check-view-doc-list fa fa-check"`;
                } else if (this.params.data.operationState === OperationState.WARNING) {
                    secondImg += ` class="second-icon exclamation-view-doc-list fa fa-exclamation-triangle"`;
                }
                if (this.params.data.iconTitle) {
                    secondImg += ` title="${this.params.data.iconTitle}"`;
                }
                secondImg += ` data-cell-action-type="${CellActionType.workbasketIcon}"></i>`;
                htmlParts.push(secondImg);
            }
        }
        this.params.value = this.params && this.params.value ? this.params.value.replace(/<exists>/g, '&lt;exists&gt;') : this.params.value;
        if (this.params.data.uploadState === 'Registered') {
            htmlParts.push(`<span style="line-height: 30;" title='${this.params.value}'>${this.params.data.colorizedDescription || this.params.value}</span>`);
        } else {
            htmlParts.push(`<span title='${this.params.value}'>${this.params.data.colorizedDescription || this.params.value}</span>`);
        }
        htmlParts.push(`</div>`);
        htmlParts.push(`<div class="buttons">`);
        if (this.isPauseable() && this.permissionControl('Pause')) {
            htmlParts.push(`<i class="action fa fa-pause" title="Pause" data-cell-action-type="${CellActionType.workbasketPause}"></i>`);
        }
        if (this.isResumeable() && this.permissionControl('Resume')) {
            htmlParts.push(`<i class="action fa fa-play" title="Resume" data-cell-action-type="${CellActionType.workbasketResume}"></i>`);
        }
        if (this.isCancelable() && this.permissionControl('Cancel')) {
            htmlParts.push(`<i class="action fa fa-close" title="Cancel" data-cell-action-type="${CellActionType.workbasketCancel}"></i>`);
        }
        if (this.isDeleteable() && this.permissionControl('Delete')) {
            htmlParts.push(`<i class="action fa fa-trash" title="Delete" data-cell-action-type="${CellActionType.workbasketDelete}"></i>`);
        }
        htmlParts.push(`</div>`);
        htmlParts.push('</span>');
        this.cellElement.innerHTML = htmlParts.join('');
        this.pauseButtonElement = this.cellElement.querySelector('.action.fa-pause');
        this.resumeButtonElement = this.cellElement.querySelector('.action.fa-play');
        this.cancelButtonElement = this.cellElement.querySelector('.action.fa-close');
        this.deleteButtonElement = this.cellElement.querySelector('.action.fa-trash');
        if (this.params.data instanceof Job) {
            const iconElement = this.cellElement.querySelector(`[data-cell-action-type="${CellActionType.workbasketIcon}"]`) as HTMLElement;
            if (iconElement) {
                iconElement.onclick = (ev: MouseEvent) => {
                    this.preventDefaultAndHandleClick(ev, CellActionType.workbasketIcon);
                }
            }
            if (this.pauseButtonElement) {
                this.pauseButtonElement.onclick = (ev: MouseEvent) => {
                    this.preventDefaultAndHandleClick(ev, CellActionType.workbasketPause);
                }
            }
            if (this.cancelButtonElement) {
                this.cancelButtonElement.onclick = (ev: MouseEvent) => {
                    this.preventDefaultAndHandleClick(ev, CellActionType.workbasketCancel);
                }
            }
            if (this.resumeButtonElement) {
                this.resumeButtonElement.onclick = (ev: MouseEvent) => {
                    this.preventDefaultAndHandleClick(ev, CellActionType.workbasketResume);
                }
            }
        } else {
            if ((this.pauseButtonElement || this.resumeButtonElement || this.cancelButtonElement || this.deleteButtonElement) && this.params.api) {
                this.params.api.addEventListener('selectionChanged', (event?: SelectionChangedEvent) => {
                    const isUnselectedOrSingleSelection = this.isUnselectedOrSingleSelection();
                    if (this.pauseButtonElement) {
                        this.pauseButtonElement.hidden = !isUnselectedOrSingleSelection;
                    }
                    if (this.resumeButtonElement) {
                        this.resumeButtonElement.hidden = !isUnselectedOrSingleSelection;
                    }
                    if (this.cancelButtonElement) {
                        this.cancelButtonElement.hidden = !isUnselectedOrSingleSelection;
                    }
                    if (this.deleteButtonElement) {
                        this.deleteButtonElement.hidden = !isUnselectedOrSingleSelection;
                    }
                });
            }
        }
    }

    preventDefaultAndHandleClick(ev: MouseEvent, actionType: CellActionType): void {
        if (!ev?.shiftKey && this.gridContext) {
            ev.preventDefault();
            ev.stopPropagation();
            this.gridContext.onTaskActionClick(actionType, this.params);
        }
    }

    getGui(): HTMLElement {
        return this.cellElement;
    }

    refresh(params: ICellRendererParams): boolean {
        return false;
    }

    onCellContextMenu = (event: MouseEvent): void => {
        if (this.params && this.gridContext && this.gridContext.onCellContextMenu) {
            this.gridContext.onCellContextMenu(this.params, event);
        }
    }

    permissionControl(action: string): boolean {
        if (this.params && this.params.permissionControl) {
            return this.params.permissionControl(action);
        }
        return false;
    }

    isUnselectedOrSingleSelection(): boolean {
        return this.params.api && this.params.node && (!this.params.node.isSelected() || this.params.api.getSelectedRows().length === 1);
    }

    isPauseable(): boolean {
        return ((this.params.data.taskStatus === TaskStatus.IN_PROGRESS &&
            this.params.data.operationState !== OperationState.PAUSED) ||
            this.params.data.operationState === OperationState.QUEUED ||
            this.params.data.operationState === OperationState.BLOCKED) &&
            this.params.value?.indexOf('Running Workflow') === -1;
    }

    isResumeable(): boolean {
        return this.params.data.taskStatus === TaskStatus.PAUSED ||
            this.params.data.operationState === OperationState.PAUSED || this.params.data.taskStatus === TaskStatus.PAUSED_ON_FAILURE;
    }

    isCancelable(): boolean {
        return this.params.data.operationState === OperationState.QUEUED ||
            this.params.data.operationState === OperationState.BLOCKED ||
            this.params.data.taskStatus === TaskStatus.IN_PROGRESS ||
            this.params.data.taskStatus === TaskStatus.PAUSED ||
            this.params.data.taskStatus === TaskStatus.PAUSED_ON_FAILURE;
    }

    isDeleteable(): boolean {
        return this.params.data.taskStatus === TaskStatus.SUCCESS ||
            this.params.data.taskStatus === TaskStatus.FAILURE ||
            this.params.data.taskStatus === TaskStatus.CANCELLED ||
            this.params.data.taskStatus === TaskStatus.FAILURE;
    }

    setIconClassViewDocList(): void {
        if (this.params.data.iconTitle === TaskStatusTitle.SUCCESS_VIEW_DOC_LIST || this.params.data.iconTitle === TaskStatusTitle.WARNING_VIEW_DOC_LIST) {
            this.params.data.iconClass += this.params.data.numberResults.localeCompare("0") != 0 ? ' view-doc-list' : ' cant-view-doc-list';
        }
    }
}

export interface ActionCellParams extends ICellRendererParams {
    permissionControl: (action: string) => boolean;
}
